import { client } from '@gustav/backend-sdk/client'
import type { QueryClient } from '@tanstack/react-query'
import { isSessionDeathCode } from './auth-recovery-codes'
import { envVars } from './env-vars'

type GetCsrfToken = (queryClient: QueryClient) => string | null | undefined
type IsSessionAuthenticated = (queryClient: QueryClient) => boolean

interface BrowserSdkConfig {
	queryClient?: QueryClient
	onUnauthorized?: () => Promise<void> | void
	getCsrfToken?: GetCsrfToken
	isSessionAuthenticated?: IsSessionAuthenticated
	requestInterceptorId?: number
	responseInterceptorId?: number
}

interface BrowserSdkConfigOptions {
	queryClient: QueryClient
	onUnauthorized: () => Promise<void> | void
	/**
	 * App-provided reader for the CSRF token; the app knows which query key
	 * holds the session (client vs admin). Returns the token or nullish.
	 */
	getCsrfToken: GetCsrfToken
	/**
	 * App-provided reader for the cached session state; the app knows which
	 * query key holds the session (client vs admin). Session recovery fires
	 * only while this is true, so bootstrap 401s (login, MFA challenge,
	 * anonymous session probe) never clear the cache.
	 */
	isSessionAuthenticated: IsSessionAuthenticated
}

const BROWSER_SDK_CONFIG_KEY = Symbol.for('react-starter.browser-sdk-config')

type ConfigurableBrowserSdkClient = typeof client & {
	[BROWSER_SDK_CONFIG_KEY]?: BrowserSdkConfig
}

export function configureBrowserSdk({
	queryClient,
	onUnauthorized,
	getCsrfToken,
	isSessionAuthenticated
}: BrowserSdkConfigOptions) {
	if (typeof window === 'undefined') {
		return
	}

	const config = getBrowserSdkConfig()
	config.queryClient = queryClient
	config.onUnauthorized = onUnauthorized
	config.getCsrfToken = getCsrfToken
	config.isSessionAuthenticated = isSessionAuthenticated

	client.setConfig({
		baseUrl: resolveBrowserSdkBaseUrl(),
		headers: buildBrowserSdkBaseHeaders(),
		credentials: 'include'
	})

	if (
		config.requestInterceptorId === undefined ||
		!client.interceptors.request.exists(config.requestInterceptorId)
	) {
		config.requestInterceptorId =
			client.interceptors.request.use(addSessionToken)
	}

	if (
		config.responseInterceptorId === undefined ||
		!client.interceptors.response.exists(config.responseInterceptorId)
	) {
		config.responseInterceptorId =
			client.interceptors.response.use(handleUnauthorized)
	}
}

function getBrowserSdkConfig() {
	const sdkClient = client as ConfigurableBrowserSdkClient
	sdkClient[BROWSER_SDK_CONFIG_KEY] ??= {}
	return sdkClient[BROWSER_SDK_CONFIG_KEY]
}

function readBrowserSdkConfig() {
	return (client as ConfigurableBrowserSdkClient)[BROWSER_SDK_CONFIG_KEY]
}

function addSessionToken(request: Request) {
	const headers = new Headers(request.headers)
	const config = readBrowserSdkConfig()
	const queryClient = config?.queryClient
	const token = queryClient ? config?.getCsrfToken?.(queryClient) : undefined

	if (!token) {
		return request
	}

	headers.set('X-CSRF-Token', token)

	return new Request(request, { headers })
}

let sessionRecovery: Promise<void> | undefined

async function handleUnauthorized(response: Response) {
	if (response.status !== 401) {
		return response
	}

	const config = readBrowserSdkConfig()
	const queryClient = config?.queryClient

	// Recover only when a session we believed alive is declared dead by the
	// backend: bootstrap 401s (login, MFA challenge, anonymous session probe)
	// arrive with a non-authenticated cache and must never clear it.
	if (!(queryClient && config.isSessionAuthenticated?.(queryClient))) {
		return response
	}

	if (!isSessionDeathCode(await readUnauthorizedErrorCode(response))) {
		return response
	}

	// Single-flight: parallel 401s from one dead session share one recovery,
	// and once it clears the cache, stragglers fail the authenticated check.
	sessionRecovery ??= recoverDeadSession(config, queryClient)
	await sessionRecovery

	return response
}

async function recoverDeadSession(
	config: BrowserSdkConfig,
	queryClient: QueryClient
) {
	try {
		queryClient.clear()
		await config.onUnauthorized?.()
	} finally {
		sessionRecovery = undefined
	}
}

async function readUnauthorizedErrorCode(response: Response) {
	try {
		const body: unknown = await response.clone().json()

		if (
			body &&
			typeof body === 'object' &&
			'code' in body &&
			typeof body.code === 'string'
		) {
			return body.code
		}
	} catch {
		// Non-JSON 401 body (proxy/infra) is not a backend session verdict.
	}

	return undefined
}

function resolveBrowserSdkBaseUrl() {
	// Dev: route through the Vite dev-proxy `/api` namespace so backend calls
	// stay same-origin (localhost) and auth cookies work. The proxy strips
	// `/api` before forwarding, so the backend still sees the SDK's bare paths.
	if (import.meta.env.DEV) {
		return '/api'
	}

	return envVars.VITE_API_ENDPOINT
}

function buildBrowserSdkBaseHeaders(): Record<string, string> {
	if (!import.meta.env.DEV) {
		return {}
	}

	return {
		'ngrok-skip-browser-warning': 'true'
	}
}
